import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import {
  ExamAttemptGateReason,
  ExamAttemptSaveStatus,
  IExamAttemptAnswerDraft,
  IExamAttemptJoinResponse,
  IExamAttemptState,
} from '../../interface/examAttempt.interface';
import {
  autosaveExamAttemptAnswer,
  joinExamAttempt,
  resumeExamAttempt,
  submitExamAttempt,
} from '../action/examAttempt.action';

const initialState: IExamAttemptState = {
  joinLoading: false,
  joinError: null,
  joinErrorCode: null,
  snapshot: null,
  attempt: null,
  expiresAtUtc: null,
  serverNowUtc: null,
  syncedAtClientMs: null,
  paperHeader: null,
  answers: {},
  saveStatus: {},
  autosaveError: null,
  submitLoading: false,
  submitError: null,
  submitted: null,
  reviewOpen: false,
};

/**
 * Server response -> local draft. `answers` is keyed by `examPaperItemId` so
 * the renderer/autosave hook can look up + patch one item at a time without
 * scanning an array on every keystroke.
 */
const applyJoinResponse = (state: IExamAttemptState, data: IExamAttemptJoinResponse['data']) => {
  state.attempt = data.attempt;
  state.snapshot = data.snapshot;
  state.expiresAtUtc = data.expiresAtUtc;
  state.serverNowUtc = data.serverNowUtc;
  state.syncedAtClientMs = performance.now();
  state.paperHeader = data.paperHeader;
  state.answers = data.answers.reduce<Record<string, IExamAttemptAnswerDraft>>((acc, answer) => {
    acc[answer.examPaperItemId] = {
      examPaperItemId: answer.examPaperItemId,
      answerText: answer.answerText ?? undefined,
      selectedOptionId: answer.selectedOptionId ?? undefined,
      drawingData: answer.drawingData ?? undefined,
      uploadIds: answer.files.map((file) => file.uploadId),
    };
    return acc;
  }, {});
};

const examAttemptSlice = createSlice({
  name: 'examAttempt',
  initialState,
  reducers: {
    // The submit-confirmation flow (CVA-4167) dispatches this on dismiss —
    // clears the attempt from memory so Back cannot re-enter. The server-side
    // refusal to serve a submitted attempt is the actual guarantee; this is
    // just hygiene.
    resetExamAttemptState: () => initialState,

    /**
     * Local-only update as the student types/selects/draws — applied
     * immediately so the UI never waits on the network, then debounced by the
     * calling hook into `autosaveExamAttemptAnswer`. Merges onto the existing
     * draft for the item: the MATH_QUESTION dual-input zone saves its text
     * and drawing channels independently, so patching one must not clobber
     * the other still held only in this local draft.
     */
    setLocalAnswerDraft: (state, action: PayloadAction<IExamAttemptAnswerDraft>) => {
      const existing = state.answers[action.payload.examPaperItemId] ?? {
        examPaperItemId: action.payload.examPaperItemId,
      };
      state.answers[action.payload.examPaperItemId] = {
        ...existing,
        ...action.payload,
      };
    },

    setSaveStatus: (
      state,
      action: PayloadAction<{ examPaperItemId: string; status: ExamAttemptSaveStatus }>,
    ) => {
      state.saveStatus[action.payload.examPaperItemId] = action.payload.status;
    },

    setReviewOpen: (state, action: PayloadAction<boolean>) => {
      state.reviewOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(joinExamAttempt.pending, (state) => {
        state.joinLoading = true;
        state.joinError = null;
      })
      .addCase(
        joinExamAttempt.fulfilled,
        (state, action: PayloadAction<IExamAttemptJoinResponse>) => {
          state.joinLoading = false;
          state.joinErrorCode = null;
          applyJoinResponse(state, action.payload.data);
        },
      )
      .addCase(joinExamAttempt.rejected, (state, action) => {
        state.joinLoading = false;
        state.joinError =
          action.payload?.message || tr('exam_attempt.error_join', 'Failed to join the exam.');
        // CVA-4197 follow-up (auto-refresh at exam start): deliberately NOT
        // reset on `.pending` above — see the field's own doc comment on
        // `IExamAttemptState`. Stays `'NOT_STARTED'` across repeated
        // identical poll failures instead of flickering through `null`.
        state.joinErrorCode = (action.payload?.errorCode as ExamAttemptGateReason) ?? null;
      })

      .addCase(resumeExamAttempt.pending, (state) => {
        state.joinLoading = true;
        state.joinError = null;
      })
      .addCase(
        resumeExamAttempt.fulfilled,
        (state, action: PayloadAction<IExamAttemptJoinResponse>) => {
          state.joinLoading = false;
          state.joinErrorCode = null;
          applyJoinResponse(state, action.payload.data);
        },
      )
      .addCase(resumeExamAttempt.rejected, (state, action) => {
        state.joinLoading = false;
        // A 404 here is the EXPECTED signal on a genuinely first-ever join —
        // resume 404s only when no attempt exists yet, and the page's own
        // mount effect immediately falls back to `joinExamAttempt` on exactly
        // this status. Surfacing it as an error would show a scary toast for
        // completely normal behavior on every first join. Leave `joinError`
        // null here; if the fallback join itself fails, `joinExamAttempt.rejected`
        // sets its own (real) error message below.
        if (action.payload?.status !== 404) {
          state.joinError =
            action.payload?.message ||
            tr('exam_attempt.error_resume', 'Failed to resume the exam.');
        }
        // Resume can never itself return NOT_STARTED (it 404s before any
        // gate check runs when no attempt exists — see exam-attempt.service.ts
        // `resumeAttempt`), so this is always `null` in practice today. Kept
        // for symmetry with `joinExamAttempt.rejected` rather than assuming
        // that will never change.
        state.joinErrorCode = (action.payload?.errorCode as ExamAttemptGateReason) ?? null;
      })

      // Autosave failure never blanks the answer already on screen — same
      // reasoning as `downloadLoading` in the sibling studentPortalExam slice.
      // Only `saveStatus`/`autosaveError` change; `answers` is untouched.
      .addCase(autosaveExamAttemptAnswer.pending, (state, action) => {
        state.saveStatus[action.meta.arg.payload.examPaperItemId] = 'saving';
      })
      .addCase(autosaveExamAttemptAnswer.fulfilled, (state, action) => {
        state.saveStatus[action.meta.arg.payload.examPaperItemId] = 'saved';
        state.serverNowUtc = action.payload.data.serverNowUtc;
        state.syncedAtClientMs = performance.now();
        state.autosaveError = null;
      })
      .addCase(autosaveExamAttemptAnswer.rejected, (state, action) => {
        const itemId = action.payload?.examPaperItemId ?? action.meta.arg.payload.examPaperItemId;
        state.saveStatus[itemId] = 'error';
        state.autosaveError =
          action.payload?.message ||
          tr('exam_attempt.error_autosave', 'Failed to save your answer.');
      })

      .addCase(submitExamAttempt.pending, (state) => {
        state.submitLoading = true;
        state.submitError = null;
      })
      .addCase(submitExamAttempt.fulfilled, (state, action) => {
        state.submitLoading = false;
        state.submitted = action.payload.data;
        if (state.attempt) {
          state.attempt.submittedAt = action.payload.data.submittedAt;
          state.attempt.submissionType = action.payload.data.submissionType;
        }
      })
      .addCase(submitExamAttempt.rejected, (state, action) => {
        state.submitLoading = false;
        state.submitError =
          action.payload?.message || tr('exam_attempt.error_submit', 'Failed to submit the exam.');
      });
  },
});

export const { resetExamAttemptState, setLocalAnswerDraft, setSaveStatus, setReviewOpen } =
  examAttemptSlice.actions;
export default examAttemptSlice.reducer;
